import React, { useMemo } from "react";
import { Box, useBreakpointValue } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  Autoplay,
  EffectCoverflow,
  Navigation,
  Pagination,
} from "swiper/modules";

export const HERO_BANNER_AUTOPLAY_DELAY = 5000;
export const HERO_QUICK_TOUR_SLIDE_COUNT = 14;

const HERO_QUICK_TOUR_BASE_URL =
  "https://assets.deepsoulai.com/main-page/hero/quick-tour";

type HeroQuickTourLanguage = "en" | "zh_cn" | "zh_hk";

export const getHeroQuickTourLanguage = (
  language?: string | null,
): HeroQuickTourLanguage => {
  const normalized = String(language || "")
    .trim()
    .toLowerCase();

  if (normalized === "en" || normalized.startsWith("en-")) return "en";
  if (
    normalized === "zh-cn" ||
    normalized.startsWith("zh-cn-") ||
    normalized === "zh-sg" ||
    normalized.startsWith("zh-sg-") ||
    normalized === "zh-hans" ||
    normalized.startsWith("zh-hans-")
  ) {
    return "zh_cn";
  }
  if (normalized === "zh" || normalized.startsWith("zh-")) return "zh_hk";
  return "zh_hk";
};

export const getHeroQuickTourImages = (language?: string | null): string[] => {
  const assetLanguage = getHeroQuickTourLanguage(language);
  return Array.from({ length: HERO_QUICK_TOUR_SLIDE_COUNT }, (_, index) => {
    const filename = String(index + 1).padStart(2, "0");
    const extension = "png";
    return `${HERO_QUICK_TOUR_BASE_URL}/${assetLanguage}/${filename}.${extension}`;
  });
};

const HeroBannerCarousel: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isDesktop = useBreakpointValue({ base: false, md: true });
  const assetLanguage = getHeroQuickTourLanguage(i18n.language);
  const images = useMemo(
    () => getHeroQuickTourImages(i18n.language),
    [i18n.language],
  );

  return (
    <Box
      data-testid="hero-banner-carousel"
      width="100%"
      overflow="hidden"
      sx={{
        ".hero-quick-tour-swiper": {
          "--swiper-navigation-color": "#ff4055",
          "--swiper-pagination-color": "#ff4055",
          "--swiper-pagination-bullet-inactive-color": "#8d8992",
          "--swiper-pagination-bullet-inactive-opacity": "0.32",
        },
        ".hero-quick-tour-swiper .swiper-button-prev, .hero-quick-tour-swiper .swiper-button-next":
          {
            width: { base: "34px", md: "44px" },
            height: { base: "34px", md: "44px" },
            marginTop: { base: "-34px", md: "-38px" },
            borderRadius: "999px",
            background: "rgba(255, 255, 255, 0.82)",
            boxShadow: "0 8px 24px rgba(40, 35, 52, 0.14)",
            backdropFilter: "blur(8px)",
          },
        ".hero-quick-tour-swiper .swiper-button-prev::after, .hero-quick-tour-swiper .swiper-button-next::after":
          {
            fontSize: { base: "14px", md: "18px" },
            fontWeight: 800,
          },
        ".hero-quick-tour-swiper .swiper-pagination": {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: { base: "3px", md: "5px" },
          paddingInline: "8px",
        },
        ".hero-quick-tour-swiper .swiper-pagination-bullet": {
          flex: "0 0 auto",
          width: { base: "6px", md: "7px" },
          height: { base: "6px", md: "7px" },
          margin: "0 !important",
          transition: "width 180ms ease, opacity 180ms ease",
        },
        ".hero-quick-tour-swiper .swiper-pagination-bullet-active": {
          width: { base: "16px", md: "20px" },
          borderRadius: "999px",
        },
      }}
    >
      <Swiper
        key={assetLanguage}
        effect="coverflow"
        grabCursor
        centeredSlides
        rewind
        slidesPerView="auto"
        coverflowEffect={{
          rotate: 50,
          stretch: 0,
          depth: 100,
          modifier: 1,
          slideShadows: true,
        }}
        pagination={{ clickable: true }}
        navigation
        autoplay={{
          delay: HERO_BANNER_AUTOPLAY_DELAY,
          disableOnInteraction: false,
        }}
        modules={[EffectCoverflow, Navigation, Pagination, Autoplay]}
        className="hero-quick-tour-swiper"
        style={{ width: "100%", paddingBottom: "38px" }}
      >
        {images.map((src, index) => (
          <SwiperSlide
            key={src}
            data-testid={`hero-banner-slide-${index + 1}`}
            style={{ width: isDesktop ? "70%" : "85%", height: "auto" }}
          >
            <Box overflow="hidden" borderRadius={{ base: "16px", md: "24px" }}>
              <img
                src={src}
                alt={t("hero.carousel.quickTourAlt", {
                  current: index + 1,
                  total: HERO_QUICK_TOUR_SLIDE_COUNT,
                })}
                loading={index === 0 ? "eager" : "lazy"}
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

export default HeroBannerCarousel;
